import React, { useEffect, useState } from 'react';

interface ProgramLoadingModalProps {
  isOpen: boolean;
  programDuration?: number; // Duration in days
}

const ProgramLoadingModal: React.FC<ProgramLoadingModalProps> = ({ isOpen, programDuration = 30 }) => {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [isHoldingAt95, setIsHoldingAt95] = useState(false);

  const stages = [
    { label: 'Initializing System...', progress: 20 },
    { label: 'Analyzing Goals & Preferences...', progress: 40 },
    { label: 'Generating Roadmap Structure...', progress: 60 },
    { label: 'Gathering Resources...', progress: 80 },
    { label: 'Finalizing Program...', progress: 100 },
  ];

  // Calculate estimated time based on program duration
  // ~0.5 seconds per day of program (30 days = 15s, 180 days = 90s)
  const estimatedSeconds = Math.max(15, Math.min(programDuration * 0.5, 120));

  // Calculate how much to increment per interval (100ms) to reach 95% at estimated time
  // We go to 95% to leave room for the actual completion
  const incrementPerTick = (95 / (estimatedSeconds * 10));

  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setStage(0);
      setIsHoldingAt95(false);
      return;
    }

    const interval = setInterval(() => {
      setProgress(prev => {
        // If we're holding at 95%, don't increment
        if (isHoldingAt95) {
          return prev;
        }

        const currentStageIndex = Math.min(stage, stages.length - 1);
        const currentStage = stages[currentStageIndex];
        if (prev >= currentStage.progress && stage < stages.length - 1) {
          setStage(s => s + 1);
        }

        const newProgress = prev + incrementPerTick;

        // When we reach 95%, start holding
        if (newProgress >= 95 && !isHoldingAt95) {
          setIsHoldingAt95(true);
          // Hold at 95% for 5-10 seconds before completing
          setTimeout(() => {
            setProgress(100);
            setStage(stages.length - 1); // Ensure we're on the final stage
          }, Math.random() * 5000 + 5000); // Random between 5-10 seconds
          return 95;
        }

        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, stage, incrementPerTick, isHoldingAt95]);

  if (!isOpen) return null;

  const currentStage = stages[Math.min(stage, stages.length - 1)];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in" />

      {/* Loading Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 pointer-events-none">
        <div className="bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-slate-800/95 border border-slate-500/40 rounded-2xl shadow-2xl backdrop-blur-xl max-w-2xl w-full p-4 sm:p-8 pointer-events-auto animate-scale-in">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-block mb-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-600/80 to-cyan-600/80 flex items-center justify-center border border-blue-500/50 shadow-lg shadow-blue-500/30">
                <span className="text-3xl sm:text-4xl">🤖</span>
              </div>
            </div>
            <h2 className="text-xl sm:text-3xl font-black font-orbitron text-white tracking-wider mb-2 uppercase">
              SYSTEM
            </h2>
            <p className="text-gray-400 font-rajdhani text-base sm:text-lg">
              Creating Your Training Program
            </p>
          </div>

          {/* Progress Bar Container */}
          <div className="relative mb-6">
            {/* Stage Label */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-orbitron text-blue-400 uppercase tracking-wider">
                {currentStage.label}
              </span>
              <span className="text-sm font-orbitron font-bold text-cyan-400">
                {Math.round(progress)}%
              </span>
            </div>

            {/* Progress Bar Background */}
            <div className="relative h-6 bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-xl overflow-hidden">
              {/* Animated Progress Fill */}
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              >
                {/* Animated shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
                
                {/* Scanning line */}
                <div className="absolute right-0 top-0 bottom-0 w-1 bg-white shadow-lg shadow-white/50" />
              </div>

              {/* Glow effect */}
              <div 
                className="absolute inset-y-0 left-0 shadow-lg shadow-blue-500/50 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Stage Indicators */}
          <div className="flex justify-between items-center mb-6">
            {stages.map((s, index) => (
              <div key={index} className="flex flex-col items-center">
                <div 
                  className={`w-3 h-3 rounded-full border-2 transition-all ${
                    index <= stage 
                      ? 'bg-blue-500 border-blue-500 shadow-lg shadow-blue-500/50' 
                      : 'bg-transparent border-slate-600'
                  }`}
                />
                {index < stages.length - 1 && (
                  <div className={`w-8 sm:w-12 h-0.5 mt-1.5 transition-all ${
                    index < stage ? 'bg-blue-500' : 'bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* System Message */}
          <div className="mt-6 p-4 bg-slate-700/50 backdrop-blur-sm border border-blue-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl animate-pulse">⚡</span>
              <div className="flex-1">
                <div className="text-xs font-orbitron text-blue-400 uppercase font-bold mb-1">
                  System Message
                </div>
                <div className="text-sm text-gray-300 font-rajdhani">
                  AI is generating a personalized training program tailored to your goals. 
                  {programDuration <= 30 && " This process takes approximately 15-30 seconds."}
                  {programDuration > 30 && programDuration <= 90 && " This process takes approximately 30-60 seconds for larger programs."}
                  {programDuration > 90 && " This process takes 60-120 seconds for comprehensive programs. Please be patient."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgramLoadingModal;

